import React, { useEffect, useRef, useState } from 'react';
import {
  LockClosedIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

/**
 * PinOnlyAuthModal Component
 * Re-authenticates cashier with PIN only after timeout/session expiry
 * Used when shift is already active but session expired
 */
export const PinOnlyAuthModal = ({
  isOpen,
  onClose,
  onVerify,
  loading,
  error,
  title = 'Session Expired',
  description = 'Enter your PIN to continue',
  cashierName = 'Cashier'
}) => {
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [localError, setLocalError] = useState('');
  const pinInputRef = useRef(null);
  const MAX_ATTEMPTS = 3;

  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      pinInputRef.current?.focus();
    }, 50);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLocalError('');

    const pinString = String(pin).trim();

    console.log('PinOnly auth modal trimmedPin', pinString);

    if (pinString.length !== 4 || !/^\d{4}$/.test(pinString)) {
      setLocalError('PIN must be exactly 4 digits');
      return;
    }

    try {
      await onVerify(pinString);
      // Reset on success
      setPin('');
      setAttempts(0);
      setLocalError('');
    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setLocalError(err.message || 'Invalid PIN');

      if (newAttempts >= MAX_ATTEMPTS) {
        setLocalError('Maximum attempts exceeded. Please contact administrator.');
        setTimeout(() => {
          handleClose();
        }, 3000);
      }
    }
  };

  const handleClose = () => {
    setPin('');
    setAttempts(0);
    setLocalError('');
    onClose();
  };

  if (!isOpen) return null;

  const isLocked = attempts >= MAX_ATTEMPTS;
  const displayError = localError || error;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <LockClosedIcon className="h-6 w-6 text-orange-600" />
            <div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500">Welcome back, {cashierName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading || isLocked}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-gray-600 text-sm">{description}</p>

          {/* Error Alert */}
          {displayError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm font-medium">{displayError}</p>
            </div>
          )}

          {/* Attempts Counter */}
          {attempts > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-2">
              <p className="text-orange-700 text-xs font-medium">
                Attempt {attempts} of {MAX_ATTEMPTS}
              </p>
            </div>
          )}

          {/* PIN Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              PIN
            </label>
            <input
              ref={pinInputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="4"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              placeholder="Enter your 4-digit PIN"
              disabled={loading || isLocked}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl tracking-widest placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">Numbers only (4 digits)</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || isLocked || pin.length !== 4}
            className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition duration-200"
          >
            {loading ? 'Verifying...' : 'Unlock POS'}
          </button>

          {isLocked && (
            <p className="text-center text-xs text-red-600 font-medium">
              Session locked. Please contact manager.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default PinOnlyAuthModal;
